import React, { useRef, useEffect, useCallback } from 'react';
import { 
    TextField, 
    Button, 
    Checkbox, 
    FormControlLabel, 
    Grid, 
    Box, 
    Typography, 
    Dialog, 
    Tooltip,
    Card,
    CardContent,
    useTheme,
    useMediaQuery,
    IconButton,
    Collapse
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import CodeIcon from '@mui/icons-material/Code';
import ImageGallery from '../../../../components/Settings/ImageGallery';

// Reusable Setting Item Component with help tooltip
const SettingItem = ({ helpText, children }) => {
    const [showHelp, setShowHelp] = React.useState(false);
    
    return (
        <Box sx={{ 
            width: '100%',
            mb: { xs: 1, sm: 1.5 }
        }}>
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1
            }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {children}
                </Box>
                {helpText && (
                    <IconButton 
                        size="small" 
                        onClick={() => setShowHelp(!showHelp)}
                        sx={{ 
                            mt: 0.5,
                            color: 'action.active',
                            '&:hover': { color: 'primary.main' }
                        }}
                    >
                        <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>
            {helpText && (
                <Collapse in={showHelp}>
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            display: 'block',
                            color: 'text.secondary',
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                            p: 1,
                            mt: 0.5,
                            lineHeight: 1.4
                        }}
                    >
                        {helpText}
                    </Typography>
                </Collapse>
            )}
        </Box>
    );
};

// Section Card Component
const SettingsSection = ({ title, icon: Icon, children }) => {
    return (
        <Card 
            elevation={0}
            sx={{ 
                mb: { xs: 2, sm: 3 },
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden'
            }}
        >
            <Box 
                sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.5,
                    px: { xs: 2, sm: 2.5 },
                    py: { xs: 1.25, sm: 1.5 },
                    bgcolor: 'grey.50',
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                }}
            >
                {Icon && <Icon sx={{ fontSize: { xs: 18, sm: 20 }, color: 'primary.main' }} />}
                <Typography 
                    variant="subtitle2" 
                    sx={{ 
                        fontWeight: 600,
                        color: 'text.primary',
                        fontSize: { xs: '0.875rem', sm: '0.9rem' }
                    }}
                >
                    {title}
                </Typography>
            </Box>
            <CardContent sx={{ 
                px: { xs: 2, sm: 2.5 },
                py: { xs: 2, sm: 2 },
                '&:last-child': { pb: { xs: 2, sm: 2 } }
            }}>
                {children}
            </CardContent>
        </Card>
    );
};

// Responsive TextField Component
const ResponsiveTextField = ({ name, value, onChange, label }) => {
    return (
        <TextField
            fullWidth
            size="medium"
            label={label}
            name={name}
            value={value || ''}
            onChange={onChange}
            variant="outlined"
            sx={{
                '& .MuiInputBase-input': {
                    fontSize: { xs: '0.95rem', sm: '0.9rem' },
                    py: { xs: 1.25, sm: 1 }
                },
                '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.9rem', sm: '0.85rem' }
                }
            }}
        />
    );
};

const KioskUITab = ({ 
    settings, 
    handleInputChange, 
    handleCustomKioskUIChange,
    serverKioskUI,
    handleRevertToOriginal,
    setShowDiffDialog,
    globalTenantId,
    isKioskUIGalleryOpen,
    handleOpenKioskUIGallery,
    handleCloseKioskUIGallery,
    handleKioskUIImageSelect,
    setKioskUITextareaRef,
    kioskUICursorPosition,
    setKioskUICursorPosition
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const textareaRef = useRef(null);

    // Pass the textarea ref to the parent so it can insert images at cursor position
    useEffect(() => {
        if (textareaRef.current) {
            setKioskUITextareaRef(textareaRef.current);
        }
    }, [setKioskUITextareaRef]);

    // Update cursor position whenever user interacts with the textarea
    const updateCursorPosition = useCallback(() => {
        const textarea = textareaRef.current;
        if (textarea && textarea.selectionStart !== undefined) {
            setKioskUICursorPosition({
                start: textarea.selectionStart,
                end: textarea.selectionEnd
            });
        }
    }, [setKioskUICursorPosition]);

    // Clear cursor position when textarea loses focus (but not when opening gallery)
    const handleBlur = useCallback((e) => {
        // Don't clear if clicking the Insert Image button
        if (e.relatedTarget?.textContent === 'Insert Image') {
            return;
        }
        setKioskUICursorPosition(null);
    }, [setKioskUICursorPosition]);

    const canInsertImage = settings.SettingCustomKioskUI && kioskUICursorPosition !== null;
    
    const textFields = [
        { name: 'SettingVisitorButtonText', label: 'Visitor Button Text' },
        { name: 'SettingDeliveryButtonText', label: 'Delivery Button Text' },
        { name: 'SettingPINButtonText', label: 'PIN Button Text' },
        { name: 'SettingChooseFromOptionsText', label: 'Choose From Options Text' },
        { name: 'SettingCCTVText', label: 'CCTV Text' },
        { name: 'SettingPoweredByText', label: 'Powered By Text' },
        { name: 'SettingWhoSpeakText', label: 'Who Speak Text' },
        { name: 'SettingCancelButtonText', label: 'Cancel Button Text' },
        { name: 'SettingPleaseChooseText', label: 'Please Choose Text' },
        { name: 'SettingDeliveryButtonNoSpeakText', label: 'Delivery No Speak Text' },
        { name: 'SettingDeliveryButtonSpeakText', label: 'Delivery Speak Text' },
        { name: 'SettingEnterPINText', label: 'Enter PIN Text' },
        { name: 'SettingWelcomeVisitorText', label: 'Welcome Visitor Text' },
        { name: 'SettingGateOpeningText', label: 'Gate Opening Text' },
        { name: 'SettingConnectingText', label: 'Connecting Text' },
        { name: 'SettingNobodyAnsweringText', label: 'Nobody Answering Text' },
        { name: 'SettingLeaveMessageText', label: 'Leave Message Text' },
        { name: 'SettingRecordingStartedText', label: 'Recording Started Text' },
        { name: 'SettingSaveAndSendText', label: 'Save And Send Text' },
        { name: 'SettingStartAgainText', label: 'Start Again Text' },
        { name: 'SettingMessageSentText', label: 'Message Sent Text' },
        { name: 'SettingYouAreConnectedText', label: 'You Are Connected Text' },
        { name: 'SettingInvalidPINText', label: 'Invalid PIN Text' },
        { name: 'SettingANPRWelcomeText', label: 'ANPR Welcome Text' },
        { name: 'SettingANPRAuthorisingText', label: 'ANPR Authorising Text' },
        { name: 'SettingQRCodeButtonText', label: 'QR Code Button Text' },
        { name: 'SettingQRCodeScanText', label: 'QR Code Scan Text' },
        { name: 'SettingQRCodePositionText', label: 'QR Code Position Text' },
    ];

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
            {/* Text Customisation Section */}
            <SettingsSection title="Text Customisation" icon={TextFieldsIcon}>
                <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                    {textFields.map((field) => (
                        <Grid item xs={12} sm={6} md={4} key={field.name}>
                            <ResponsiveTextField
                                name={field.name}
                                value={settings[field.name]}
                                onChange={handleInputChange}
                                label={field.label}
                            />
                        </Grid>
                    ))}
                </Grid>
            </SettingsSection>

            {/* Custom UI Section */}
            <SettingsSection title="Custom UI" icon={CodeIcon}>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12}>
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            justifyContent: 'space-between',
                            gap: 2,
                            mb: 2
                        }}>
                            <FormControlLabel
                                control={
                                    <Checkbox 
                                        name="SettingCustomKioskUI" 
                                        checked={settings.SettingCustomKioskUI || false} 
                                        onChange={handleCustomKioskUIChange}
                                        sx={{
                                            '& .MuiSvgIcon-root': {
                                                fontSize: { xs: 24, sm: 24 }
                                            }
                                        }}
                                    />
                                }
                                label={
                                    <Typography 
                                        variant="body2" 
                                        sx={{ fontSize: { xs: '0.9rem', sm: '0.9rem' } }}
                                    >
                                        Enable Custom Kiosk UI
                                    </Typography>
                                }
                                sx={{ m: 0 }}
                            />
                            
                            {settings.SettingCustomKioskUI && (
                                <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    alignItems: { xs: 'flex-start', sm: 'center' },
                                    gap: 1
                                }}>
                                    <Typography 
                                        sx={{ 
                                            cursor: 'pointer', 
                                            textDecoration: 'underline',
                                            fontSize: { xs: '0.85rem', sm: '0.875rem' },
                                            color: 'text.secondary',
                                            '&:hover': {
                                                color: 'primary.main'
                                            }
                                        }}
                                        onClick={() => setShowDiffDialog(true)}
                                    >
                                        {settings.SettingKioskUI === serverKioskUI ? 
                                            "No changes from default" : 
                                            "View changes from default"}
                                    </Typography>
                                    {settings.SettingKioskUI !== serverKioskUI && (
                                        <Button 
                                            variant="outlined" 
                                            size="small"
                                            color="warning"
                                            onClick={handleRevertToOriginal}
                                            sx={{ 
                                                fontSize: { xs: '0.8rem', sm: '0.8rem' },
                                                textTransform: 'none'
                                            }}
                                        >
                                            Revert to original
                                        </Button>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 2, 
                            mb: 2, 
                            alignItems: { xs: 'flex-start', sm: 'center' }
                        }}>
                            <Tooltip 
                                title={!settings.SettingCustomKioskUI 
                                    ? "Enable Custom Kiosk UI first" 
                                    : !kioskUICursorPosition 
                                        ? "Click in the editor where you want to insert the image" 
                                        : "Insert an image at the cursor position"
                                }
                            >
                                <span>
                                    <Button 
                                        variant="outlined" 
                                        onClick={handleOpenKioskUIGallery}
                                        disabled={!canInsertImage}
                                        sx={{ 
                                            py: { xs: 1, sm: 0.75 },
                                            fontSize: { xs: '0.875rem', sm: '0.8rem' },
                                            textTransform: 'none'
                                        }}
                                    >
                                        Insert Image
                                    </Button>
                                </span>
                            </Tooltip>
                            {settings.SettingCustomKioskUI && !kioskUICursorPosition && (
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        color: 'text.secondary',
                                        fontSize: { xs: '0.8rem', sm: '0.85rem' }
                                    }}
                                >
                                    Click in the editor where you want to insert an image
                                </Typography>
                            )}
                        </Box>

                        <TextField
                            fullWidth
                            label="Kiosk UI (HTML)"
                            name="SettingKioskUI"
                            value={settings.SettingKioskUI || ''}
                            onChange={(e) => {
                                handleInputChange(e);
                                setTimeout(updateCursorPosition, 0);
                            }}
                            variant="outlined"
                            multiline 
                            disabled={!settings.SettingCustomKioskUI} 
                            minRows={8}
                            maxRows={20}
                            inputRef={textareaRef}
                            onClick={updateCursorPosition}
                            onKeyUp={updateCursorPosition}
                            onSelect={updateCursorPosition}
                            onBlur={handleBlur}
                            onKeyDown={(e) => {
                                if (e.key === 'Tab') {
                                    e.preventDefault();
                                    const textarea = e.target;
                                    const start = textarea.selectionStart;
                                    const end = textarea.selectionEnd;
                                    textarea.value = textarea.value.substring(0, start) + '\t' + textarea.value.substring(end);
                                    textarea.selectionStart = textarea.selectionEnd = start + 1;
                                    updateCursorPosition();
                                }
                            }}
                            sx={{
                                '& .MuiInputBase-input': {
                                    fontFamily: 'monospace',
                                    fontSize: { xs: '0.8rem', sm: '0.85rem' }
                                }
                            }}
                        />
                    </Grid>
                </Grid>

                <Dialog
                    open={isKioskUIGalleryOpen}
                    onClose={handleCloseKioskUIGallery}
                    fullWidth
                    maxWidth="lg"
                >
                    <ImageGallery
                        onImageSelect={handleKioskUIImageSelect}
                        tenantId={globalTenantId}
                        pickerMode={true}
                    />
                </Dialog>
            </SettingsSection>
        </Box>
    );
};

export default KioskUITab;
